<script setup lang="ts">
import { computed, onMounted, ref, watch, nextTick } from 'vue'
import { useIntersectionObserver, useResizeObserver } from '@vueuse/core'
import { Listbox, ListboxButton, ListboxOption, ListboxOptions } from '@headlessui/vue'
import { CheckIcon, ChevronUpDownIcon, ArrowRightIcon, ArrowLeftIcon } from '@heroicons/vue/20/solid'
import { throttle } from 'lodash-es'
import type { Tab } from '~/definitions/AllDefinitions'
import { scrollToElementById } from '~/classes/Utils'

const isClient = ref(false)
onMounted(() => {
  isClient.value = true
})

interface TwTabsInterface {
  disabled?: boolean
  label?: string
  tabs: Tab[]
}

const props = withDefaults(defineProps<TwTabsInterface>(), {
  disabled: false,
  label: '',
})

// const emit = defineEmits(['change-tab'])
// Sticky behavior tracking
const isSticky = ref(false)
const tabsContainer = ref<HTMLElement | null>(null)
const navbar = ref<HTMLElement | null>(null)
const scrollContainer = ref<HTMLElement | null>(null)
const tabOriginalOffsetTop = ref(0) // Track the original offset of the tabs
const tabsHeight = ref(0)
// Active tab tracking
const currentTab = ref(props.tabs[0]?.name || '')
const sectionRefs = ref<HTMLElement[]>([])
const navbarHeight = ref(0)
const contentScrollWidth = ref(0)
// Compute selected label
const selectedLabel = computed(() => {
  return props.tabs.find(tab => tab.name === currentTab.value)?.name || '---'
})
const contentOverflow = ref(false)
const desktopTabsContainer = ref<HTMLElement | null>(null)
const desktopTabsWrapper = ref<HTMLElement | null>(null)
const arrowLeft = ref<HTMLElement | null>(null)
const arrowRight = ref<HTMLElement | null>(null)
const isArrowLeftDisabled = ref(true)
const isArrowRightDisabled = ref(false)

// Handle tab change from dropdown
function handleUpdateSelected(newValue: Tab) {
  currentTab.value = newValue.name
  scrollToElementById(
    newValue.value,
    -navbarHeight.value + ((-(tabsContainer.value?.offsetHeight || 0) * 2) - 20), // 20 to match mb-5 between cats
    scrollContainer.value!,
  )
}

// Scroll to section when tab is clicked
function scrollToSection(tab: Tab) {
  scrollToElementById(
    tab.value,
    -navbarHeight.value + ((-(tabsContainer.value?.offsetHeight || 0) * 2) - 20), // 20 to match mb-5 between cats
    scrollContainer.value!,
  )
}
function scrollArrow(direction = 'left') {
  const container = desktopTabsContainer.value
  if (!container) {
    return
  }
  container.scrollBy({ left: direction === 'left' ? -150 : 150, behavior: 'smooth' })
  updateArrowsState()
}
// Set up DOM-dependent features (client-only)
function initTabsDom() {
  nextTick(() => {
    setupIntersectionObservers()
    if (desktopTabsContainer.value && desktopTabsWrapper.value) {
      tabsHeight.value = tabsContainer.value?.getBoundingClientRect().height || 1
      updateContentOverflow()
      if (contentOverflow.value && arrowLeft.value && arrowRight.value) {
        desktopTabsContainer.value.addEventListener('scroll', throttle(() => {
          updateArrowsState()
        }, 250))
      }
    }
  })
}
function setupIntersectionObservers() {
  props.tabs.forEach((tab) => {
    // todo make this part part of props
    sectionRefs.value[tab.value] = document.getElementById(tab.value) as HTMLElement
    if (sectionRefs.value[tab.value]) {
      useIntersectionObserver(sectionRefs.value[tab.value]!, ([entry]) => {
        if (entry.isIntersecting) {
          currentTab.value = tab.name
        }
      }, {
        root: scrollContainer.value,
        rootMargin: '0px 0px -50%',
        threshold: 1,
      })
    }
  })
}
function updateArrowsState() {
  if (!desktopTabsContainer.value) {
    return
  }
  const { scrollLeft } = desktopTabsContainer.value
  isArrowLeftDisabled.value = scrollLeft <= 0
  const currentScroll = scrollLeft / (contentScrollWidth.value)
  isArrowRightDisabled.value = (1 - currentScroll) <= 0
}
function updateContentOverflow() {
  if (desktopTabsContainer.value) {
    contentOverflow.value = (desktopTabsContainer.value.offsetWidth < desktopTabsContainer.value.scrollWidth)
  }
}
// Watch for tab updates
watch(
  () => props.tabs,
  () => {
    currentTab.value = props.tabs[0]?.name || ''
    initTabsDom()
  },
  { deep: true },
)
// Sticky Observer (Throttled to prevent looping)
const checkSticky = throttle(() => {
  if (!tabsContainer.value || !navbar.value) {
    return
  }
  const scrollY = scrollContainer.value?.scrollTop || 0

  // Get the latest navbar height
  navbarHeight.value = navbar.value.offsetHeight

  // Ensure we correctly set the original offset of the tabs once
  if (tabOriginalOffsetTop.value === null) {
    tabOriginalOffsetTop.value =
      tabsContainer.value.getBoundingClientRect().top +
      scrollY -
      navbarHeight.value
  } // Make tabs sticky **below** the navbar
  isSticky.value = scrollY >= navbarHeight.value
}, 100) // Runs at most every 100ms
// Initialize on mount
onMounted(() => {
  nextTick(() => {
    navbar.value = document.querySelector('nav')
    tabsContainer.value = document.querySelector('.tw-tabs-container')
    if (navbar.value) {
      navbarHeight.value = navbar.value.offsetHeight
      scrollContainer.value = document.querySelector('.overflow-y-scroll')
      scrollContainer.value?.addEventListener('scroll', checkSticky, { passive: true })
    }

    if (tabsContainer.value) {
      // Compute original position relative to scroll container
      tabOriginalOffsetTop.value =
        tabsContainer.value.getBoundingClientRect().top +
        (scrollContainer.value?.scrollTop || 0) -
        navbarHeight.value
    }
    initTabsDom()
    // resize listener
    useResizeObserver(desktopTabsContainer.value, throttle(() => {
      requestAnimationFrame(() => {
        const scrollWidth = desktopTabsContainer.value?.scrollWidth || 0
        const offsetWidth = desktopTabsWrapper.value?.offsetWidth || 0
        contentScrollWidth.value = scrollWidth - offsetWidth
        updateContentOverflow()
        updateArrowsState()
      })
    }, 100))
  })
})
</script>

<template>
  <!-- Placeholder to prevent jump when sticky -->
  <div
    v-if="isSticky"
    :style="{ height: `${tabsHeight}px` }"
  />
  <div
    ref="tabsContainer"
    class="tw-tabs-container transition-all duration-300 "
    :class="{ 'sticky-tabs shadow-md bg-tw-2': isSticky }"
    :style="{ top: isSticky ? `${navbarHeight}px` : 'unset' }"
  >
    <!-- Mobile Dropdown: SSR fallback (static) -->
    <div
      v-if="!isClient && tabs.length"
      class="sm:hidden relative"
    >
      <div
        v-if="label"
        class="label-tw-1 mb-1"
      >
        {{ label }}
      </div>
      <div class="simple-select-input">
        <span class="block truncate">{{ tabs[0]?.name || '---' }}</span>
        <span class="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
          <ChevronUpDownIcon
            class="h-5 w-5 text-gray-400"
            aria-hidden="true"
          />
        </span>
      </div>
    </div>
    <!-- Mobile Dropdown: Client (interactive HeadlessUI) -->
    <div
      v-if="isClient && tabs.length"
      class="sm:hidden relative"
      :class="{
        'tw-container py-4': isSticky,
      }"
    >
      <div
        v-if="label"
        class="label-tw-1 mb-1"
      >
        {{ label }}
      </div>
      <Listbox
        v-model="currentTab"
        as="div"
        :disabled="disabled"
        @update:model-value="handleUpdateSelected"
      >
        <div class="relative">
          <ListboxButton
            class="simple-select-input"
            :class="{ 'cursor-not-allowed opacity-40': disabled }"
          >
            <span class="block truncate">{{ selectedLabel }}</span>
            <span class="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
              <ChevronUpDownIcon
                class="h-5 w-5 text-gray-400"
                aria-hidden="true"
              />
            </span>
          </ListboxButton>
          <transition
            leave-active-class="transition ease-in duration-100"
            leave-from-class="opacity-100"
            leave-to-class="opacity-0"
          >
            <ListboxOptions
              class="
              absolute
              z-10
              mt-1
              max-h-60
              w-full
              overflow-auto
              rounded-md
              bg-tw-3
              py-1 text-base
              shadow-lg
              ring-1
              ring-black
              ring-opacity-5
              focus:outline-none
              sm:text-sm"
            >
              <ListboxOption
                v-for="tab in tabs"
                :key="tab.name"
                v-slot="{ active }"
                as="template"
                :value="tab"
              >
                <li
                  :class="{ 'bg-tw-2': active }"
                  class="relative cursor-default select-none py-2 pl-3 pr-9"
                >
                  <span
                    :class="{
                      'font-semibold text-accent': tab.name === currentTab,
                      'font-normal text-tw-1': tab.name !== currentTab }"
                    class="block-truncate"
                  >
                    {{ tab.name }}
                  </span>
                  <span
                    v-if="tab.name === currentTab"
                    class="absolute inset-y-0 right-2 flex items-center text-accent"
                  >
                    <CheckIcon
                      class="h-5 w-5"
                      aria-hidden="true"
                    />
                  </span>
                </li>
              </ListboxOption>
            </ListboxOptions>
          </transition>
        </div>
      </Listbox>
    </div>
    <!-- Desktop Tabs -->
    <div
      class="hidden sm:block"
      :class="{ 'tw-container': isSticky }"
    >
      <div
        ref="desktopTabsWrapper"
        class="border-b border-tw-4 relative"
        :class="{ 'px-6': contentOverflow }"
      >
        <div
          v-if="contentOverflow"
          id="tabs-shadow--left"
          class="h-5 w-5 arrow-container bg-tw-1"
          :class="{
            'bg-tw-2': isSticky,
            'bg-tw-1': !isSticky,
          }"
        >
          <button
            ref="arrowLeft"
            type="button"
            :disabled="isArrowLeftDisabled"
            class="h-5 w-5 disabled:opacity-40 disabled:cursor-not-allowed"
            aria-label="Scroll left"
            @click="scrollArrow()"
          >
            <ArrowLeftIcon />
          </button>
        </div>
        <div
          v-if="contentOverflow"
          id="tabs-shadow--right"
          class="h-5 w-5 arrow-container"
          :class="{
            'bg-tw-2': isSticky,
            'bg-tw-1': !isSticky,
          }"
          @click="scrollArrow('right')"
        >
          <button
            ref="arrowRight"
            type="button"
            :disabled="isArrowRightDisabled"
            class="h-5 w-5 disabled:opacity-40 disabled:cursor-not-allowed"
            aria-label="Scroll right"
            @click="scrollArrow()"
          >
            <ArrowRightIcon />
          </button>
        </div>
        <nav
          ref="desktopTabsContainer"
          class="-mb-px flex space-x-8 overflow-x-scroll desktop-tabs-container relative"
          aria-label="Tabs"
        >
          <button
            v-for="tab in tabs"
            :key="tab.name"
            :disabled="currentTab === tab.name || disabled"
            type="button"
            :aria-current="currentTab === tab.name ? 'page' : undefined"
            :class="{
              'border-accent text-tw-1': currentTab === tab.name,
              'border-transparent text-gray-500': currentTab !== tab.name,
              'hover:border-gray-300 hover:text-gray-700': currentTab !== tab.name && !disabled,
            }"
            class="whitespace-nowrap border-b-2 py-4 px-1 text-sm font-medium disabled:cursor-not-allowed"
            @click="scrollToSection(tab)"
          >
            {{ tab.name }}
          </button>
        </nav>
      </div>
    </div>
  </div>
</template>

<style>
.sticky-tabs {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  z-index: 20;
}
.arrow-container {
  position: absolute;
  top: 0;
  z-index: 30;
  width: 24px;
  height: 100%;
  display: flex;
  align-items: center;
}
#tabs-shadow--left {
  left: 0;
}
#tabs-shadow--right {
  right: 0;
}
</style>
